import { ChangeDetectorRef, Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { SubscriptSizing } from '@angular/material/form-field';
import { InputValueType, SelectInputModel, SelectTreeInputModel, SelectTreeOptionModel } from '@libs/form/models';
import { InputBaseComponent } from '../../../models';
import { InputProviderFactory } from '../ng-input-provider';
import { SelectComponent } from '../select';

@Component({
  selector: 'ui-form-select-tree',
  templateUrl: './select-tree.component.html',
  styleUrls: ['./select-tree.component.scss'],
  providers: [
    ...InputProviderFactory.getProviders(SelectTreeComponent, true),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SelectComponent,
    ReactiveFormsModule,
  ],
})
export class SelectTreeComponent extends InputBaseComponent implements OnInit, Validator {
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Input()
  public data!: SelectTreeInputModel;

  public valueAccessor!: never;

  public selectedNodes: SelectTreeOptionModel[] = [];
  public inputItems: SelectInputModel[] = [];

  public override ngOnInit(): void {
    super.ngOnInit();

    this.addSelectedNode(this.data as SelectTreeOptionModel);
    this.addSelectedNodeInput(this.data as SelectTreeOptionModel, this.data.subscriptSizing);

    if (this.data.attributes?.required) {
      this.control.addValidators(this.validate.bind(this));
      this.control.updateValueAndValidity();
    }
  }

  public override validate(): ValidationErrors | null {
    if (!this.data.attributes?.required) {
      return null;
    }

    const lastSelected: SelectTreeOptionModel = this.selectedNodes[this.selectedNodes.length - 1];

    return lastSelected.options?.length
      ? { required: true }
      : null;
  }

  public onSelectionChange(index: number, value: InputValueType): void {
    this.selectedNodes = this.selectedNodes.slice(0, index + 1);
    this.inputItems = this.inputItems.slice(0, index + 1);

    const changedOption: SelectTreeOptionModel = this.selectedNodes[index];
    const selectedChildOption: SelectTreeOptionModel | undefined = changedOption.options?.find(
      child => child.value === value || child.label === value,
    );

    if (selectedChildOption) {
      this.addSelectedNode(selectedChildOption);

      if (selectedChildOption.options?.length) {
        this.addSelectedNodeInput(
          selectedChildOption,
          this.data.subscriptSizing,
        );
      }
    }

    const selectedValue: (string | number)[] = this.selectedNodes
      .slice(1)
      .map(c => c.value);

    this.onTouchedCallback();
    this.onChangeCallback(selectedValue.length ? selectedValue : null);
  }

  public override writeValue(value: string[] | null): void {
    const inputValue: string[] = value ?? [''];

    this.selectedNodes = [this.selectedNodes[0]];
    this.inputItems = [this.inputItems[0]];

    for (let i: number = 0; i < inputValue.length; i += 1) {
      const currentNode: SelectTreeOptionModel = this.selectedNodes[i];
      const currentNodeInput: SelectInputModel = this.inputItems[i];

      const valueNode: SelectTreeOptionModel | undefined = currentNode.options?.find(
        option => option.value === inputValue[i],
      );

      if (!valueNode) {
        break;
      }

      currentNodeInput.value = inputValue[i];
      this.addSelectedNode(valueNode);

      if (!valueNode.options?.length) {
        break;
      }

      this.addSelectedNodeInput(
        valueNode,
        this.data.subscriptSizing,
      );
    }

    this.changeDetector.detectChanges();
  }

  public override registerOnChange(fn: (_: unknown) => unknown): void {
    this.onChangeCallback = fn;
  }

  public override registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public override setDisabledState(isDisabled: boolean): void {
    this.inputItems.forEach(item => {
      item.attributes = {
        ...item.attributes,
        disabled: isDisabled,
      };
    });
  }

  private addSelectedNode(node: SelectTreeOptionModel): void {
    this.selectedNodes.push(node);
  }

  private addSelectedNodeInput(node: SelectTreeOptionModel, subscriptSizing: SubscriptSizing = 'fixed'): void {
    const level: number = this.inputItems.length;
    const inputName: string = `${this.data.name}-${level}`;
    const inputLabel: string = node.optionsLabel ?? node.label;
    const emptyOption: string | undefined = this.data.emptyOption;
    const defaultValue: string | undefined = level > 0 ? '' : undefined;

    this.inputItems.push({
      type: 'select',
      name: inputName,
      label: inputLabel,
      hint: node.hint,
      description: node.description,
      options: node.options ?? [],
      emptyOption: emptyOption,
      value: defaultValue,
      attributes: {
        required: this.data.attributes?.required,
        disabled: this.data.attributes?.disabled,
        readOnly: this.data.attributes?.readOnly,
      },
      subscriptSizing,
    });
  }
}
